import type { ReactElement } from 'react';
import React from 'react';
import { StringParam, useQueryParams } from 'use-query-params';
import Loading from 'atom-components/loading';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import SystemErrorAlert from 'feature-components/system-error-alert';
import { ascendingOrder } from 'helpers/tanstack-query.helpers';
import useMarkForMarking from 'hooks/marking/use-mark-for-marking';
import useRubricItemsForMarking from 'hooks/marking/use-rubric-items-for-marking';
import useTestItemsForMarking from 'hooks/marking/use-test-items-for-marking';
import useUICategoriesForMarking from 'hooks/marking/use-ui-categories-for-marking';
import type { AuthorisableMark } from 'policies/entities/mark';
import type { MeCustomerUser } from 'services/assist-api/entities/me/me';
import { useTags } from 'services/assist-api/entities/tag/tags';
import type { BaseTag } from 'services/assist-api/entities/tag/tags';
import type { TestInstanceResponseMarkWithItems } from 'services/assist-api/entities/test-instance-response-mark/types';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import type { TestInstanceResponseWithResponseItemsAndMediaObjects } from 'services/assist-api/entities/test-instance-response/types';
import type { BaseTestInstance } from 'services/assist-api/entities/test-instance/types';
import { isInitialLoad } from 'utils/check-query-status';
import MarkResponseForm from './mark-response-form';

type Props = {
  onSave: () => void;
  assessment: BaseTestInstance;
  response: TestInstanceResponseWithResponseItemsAndMediaObjects;
  rubricId: number;
  authorisableMark: AuthorisableMark;
  currentOrganisationUser: MeCustomerUser;
  currentUserMarker: TestInstanceResponseMarkerWithCustomerUser | undefined;
  activeMarker?: TestInstanceResponseMarkerWithCustomerUser;
  mark?: TestInstanceResponseMarkWithItems;
};

export default function MarkResponseFormLoader({
  onSave,
  assessment,
  response,
  rubricId,
  authorisableMark,
  currentOrganisationUser,
  currentUserMarker,
  activeMarker,
  mark
}: Props): ReactElement {
  const [queryParams] = useQueryParams({
    oldMarkId: StringParam
  });

  const testItemsQuery = useTestItemsForMarking(assessment, response);
  const testRubricItemsQuery = useRubricItemsForMarking(rubricId);
  const categories = useUICategoriesForMarking(
    testItemsQuery.data?.json,
    response.responseItems,
    testRubricItemsQuery.data?.json
  );

  const tagsQuery = useTags<BaseTag>(
    {
      filters: { type: 'markingAttentionReason' },
      order: ascendingOrder
    },
    { enabled: true }
  );

  // Get the old mark that is intended to be replaced (if there is one) so that we can pre-populate
  // the mark items with any scores associated with the old mark.
  const oldTestInstanceResponseMarkQuery = useMarkForMarking(queryParams.oldMarkId);

  const queriesToLoad = [
    testItemsQuery,
    testRubricItemsQuery,
    tagsQuery,
    oldTestInstanceResponseMarkQuery
  ];
  const isLoading = isInitialLoad(...queriesToLoad);
  const isError = queriesToLoad.some((q) => q.isError);

  const attentionReasons = tagsQuery.data?.json;

  return (
    <>
      {isLoading && <Loading />}
      {!isLoading && isError && <LoadingErrorAlert queries={queriesToLoad} />}
      {!isLoading && !isError && (
        <>
          {!testItemsQuery.data?.json && (
            <SystemErrorAlert>
              No test items that could be marked were found, so the response can't be marked.
            </SystemErrorAlert>
          )}
          {!testRubricItemsQuery.data?.json && (
            <SystemErrorAlert>
              No rubric items were found for the rubric, so the response can't be marked.
            </SystemErrorAlert>
          )}
          {!attentionReasons && (
            <SystemErrorAlert>
              No attention reasons were found, so the response can't be marked.
            </SystemErrorAlert>
          )}
          {categories && attentionReasons && (
            <MarkResponseForm
              onSave={onSave}
              responseId={response.id}
              rubricId={rubricId}
              categories={categories}
              attentionReasons={attentionReasons}
              authorisableMark={authorisableMark}
              currentOrganisationUser={currentOrganisationUser}
              currentUserMarker={currentUserMarker}
              activeMarker={activeMarker}
              mark={mark}
              oldMark={oldTestInstanceResponseMarkQuery.data?.json}
            />
          )}
        </>
      )}
    </>
  );
}
